/**
 * Triggers a browser file download for an in-memory blob.
 *
 * Creates a temporary object URL, programmatically clicks an anchor, then
 * revokes the URL. Used by mutations that receive a generated artefact
 * (e.g. a transaction statement export) and need to hand it to the user as
 * a saved file without persisting anything server-side.
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName
  anchor.click()
  URL.revokeObjectURL(objectUrl)
}

const ENCODED_FILE_NAME = /filename\*\s*=\s*(?:UTF-8|utf-8)?''([^;]+)/
const PLAIN_FILE_NAME = /filename\s*=\s*("?)([^";]+)\1/

/**
 * File name from a `Content-Disposition` header, or null when absent.
 * Prefers the RFC 5987 `filename*=UTF-8''…` form over plain `filename=`.
 *
 * Note: the browser only exposes this header to JS when the server lists it
 * in `Access-Control-Expose-Headers` — a null here with the header visible in
 * the network tab means the backend's CORS config is missing it.
 */
export function readFileNameFromContentDisposition(header: unknown): string | null {
  if (typeof header !== 'string' || !header) return null

  const encoded = ENCODED_FILE_NAME.exec(header)?.[1]
  if (encoded) {
    try {
      return stripFolders(decodeURIComponent(encoded.trim()))
    } catch {
      // Malformed percent-encoding — fall through to the plain form.
    }
  }

  return stripFolders(PLAIN_FILE_NAME.exec(header)?.[2]?.trim() ?? '')
}

/** `receipts/2026/orde66.pdf` → `orde66.pdf` — the server's folders aren't part of the name. */
const stripFolders = (name: string): string | null => name.split(/[\\/]/).pop()?.trim() || null
